import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useColors } from "../constants/Colors";
import { useSignupStore, UploadedImage } from "../hooks/useSignupStore";
import { useAuth } from "../contexts/AuthContext";
import {
  trpcReact,
  setTurnstileToken,
  clearTurnstileToken,
} from "../trpc_client";
import { useTurnstile } from "../hooks/useTurnstile";
import TurnstileWidget from "../components/TurnstileWidget";
import AuthModal from "../components/AuthModal";
import { usePostHog } from "posthog-js/react";

// Step components
import NameStep from "../components/signup/NameStep";
import ImagesStep from "../components/signup/ImagesStep";
import BirthdateStep from "../components/signup/BirthdateStep";
import ProfileTextStep from "../components/signup/ProfileTextStep";

const NUMBER_OF_STEPS = 4;

const SignUp: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const store = useSignupStore();
  const { user } = useAuth();
  const posthog = usePostHog();
  const {
    containerNeutralSolidSurface,
    containerNeutralLightMedium,
    textNeutralDefault,
    textNeutralMuted,
    accentColor,
    textNeutralOnPrimary,
  } = useColors();

  const {
    hasError: turnstileError,
    resetToken,
    siteKey,
    widgetRef,
    onExpire,
    onError,
  } = useTurnstile();

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showTurnstileModal, setShowTurnstileModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const utils = trpcReact.useUtils();
  const createUserMutation = trpcReact.createUser.useMutation();

  const { data: userExists, isLoading: checkingUser } =
    trpcReact.checkUserExists.useQuery(undefined, {
      enabled: !!user,
    });

  useEffect(() => {
    if (user && userExists?.exists && !checkingUser) {
      navigate("/profile", { replace: true });
    }
  }, [user, userExists, checkingUser, navigate]);

  useEffect(() => {
    document.body.style.backgroundColor = containerNeutralSolidSurface;
    document.documentElement.style.backgroundColor =
      containerNeutralSolidSurface;
    return () => {
      document.body.style.backgroundColor = "";
      document.documentElement.style.backgroundColor = "";
    };
  }, [containerNeutralSolidSurface]);

  useEffect(() => {
    if (store.step === 1 && !user) {
      setShowAuthModal(true);
    }
  }, [store.step, user]);

  useEffect(() => {
    posthog?.capture("signup_step_viewed", {
      surface: "signup_page",
      step: store.step,
      is_authenticated: !!user,
    });
  }, [posthog, store.step, user]);

  const createUser = async () => {
    if (!user) return false;

    if (store.images.some((img: UploadedImage) => img.progress < 1)) {
      posthog?.capture("sign_up_failed", {
        method: "email",
        code: "upload_in_progress",
        message: "Profile image uploads are still in progress",
      });
      posthog?.capture("signup_step_validation_blocked", {
        surface: "signup_page",
        step: store.step,
        reason: "upload_in_progress",
        is_authenticated: !!user,
      });
      setSubmitError(
        t("wait_for_uploads") || "Please wait for all images to upload",
      );
      return false;
    }

    setSubmitError(null);
    posthog?.capture("turnstile_modal_shown", {
      surface: "signup_page",
      step: store.step,
      is_authenticated: !!user,
    });
    resetToken();
    setShowTurnstileModal(true);
    return false;
  };

  const handleTurnstileSuccess = async (token: string) => {
    setShowTurnstileModal(false);
    if (!user) return;

    posthog?.capture("turnstile_success", {
      surface: "signup_page",
      step: store.step,
      is_authenticated: true,
    });
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      posthog?.capture("signup_profile_submit_started", {
        surface: "signup_page",
        step: store.step,
        is_authenticated: true,
      });
      setTurnstileToken(token);
      await createUserMutation.mutateAsync({
        firebase_id: user.uid,
        email: user.email || "",
        phone_number: user.phoneNumber || "",
        username: store.name.trim(),
        gender: store.gender,
        birthdate: store.birthdate?.toISOString() || new Date().toISOString(),
        profile_text: store.profileText.trim(),
        profile_interests: [],
        profile_object_names: store.images
          .filter((img: UploadedImage) => img.object_name)
          .map((img: UploadedImage) => img.object_name),
      });

      await utils.checkUserExists.refetch();
      await utils.getCurrentUserProfile.refetch();

      posthog?.capture("signup_profile_submit_success", {
        surface: "signup_page",
        step: store.step,
        is_authenticated: true,
      });
      store.reset();
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Error creating user:", error);
      const errorMessage = error instanceof Error ? error.message : "";
      posthog?.capture("sign_up_failed", {
        method: "email",
        code: "profile_create_failed",
        message: errorMessage || "Failed to create account profile",
      });
      posthog?.capture("signup_profile_submit_failed", {
        surface: "signup_page",
        step: store.step,
        is_authenticated: true,
        code: "profile_create_failed",
        message: errorMessage || "Failed to create account profile",
      });
      setSubmitError(
        errorMessage ||
          t("signup.create_error") ||
          "Failed to create account. Please try again.",
      );
    } finally {
      clearTurnstileToken();
      resetToken();
      setIsSubmitting(false);
    }
  };

  const handleNext = async () => {
    posthog?.capture("signup_step_next_clicked", {
      surface: "signup_page",
      step: store.step,
      is_authenticated: !!user,
    });

    if (!store.isStepValid(store.step)) {
      posthog?.capture("signup_step_validation_blocked", {
        surface: "signup_page",
        step: store.step,
        reason: "invalid_step",
        is_authenticated: !!user,
      });
      return;
    }

    if (store.step === NUMBER_OF_STEPS - 1) {
      await createUser();
      return;
    }

    if (store.step < NUMBER_OF_STEPS - 1) {
      store.nextStep();
    }
  };

  const handleBack = () => {
    posthog?.capture("signup_step_back_clicked", {
      surface: "signup_page",
      step: store.step,
      is_authenticated: !!user,
    });
    store.prevStep();
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
  };

  const handleAuthRequired = () => {
    setShowAuthModal(true);
  };

  const renderStep = () => {
    switch (store.step) {
      case 0:
        return <NameStep store={store} />;
      case 1:
        return <ImagesStep store={store} onAuthRequired={handleAuthRequired} />;
      case 2:
        return <BirthdateStep store={store} />;
      case 3:
        return <ProfileTextStep store={store} />;
      default:
        return null;
    }
  };

  const isCurrentStepValid = store.isStepValid(store.step);
  const isLastStep = store.step === NUMBER_OF_STEPS - 1;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "Enter" &&
        !isLastStep &&
        isCurrentStepValid &&
        !isSubmitting
      ) {
        const target = e.target as HTMLElement;
        if (target.tagName === "TEXTAREA") return;

        e.preventDefault();
        handleNext();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isLastStep, isCurrentStepValid, isSubmitting, store.step]);

  return (
    <div
      className="h-screen flex flex-col font-gnarly-regular relative overflow-hidden"
      style={{
        backgroundColor: containerNeutralSolidSurface,
        color: textNeutralDefault,
      }}
    >
      {/* Subtle ambient gradient */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 80% 50% at 50% 20%, rgba(91, 61, 246, 0.035) 0%, transparent 70%)`,
        }}
      />

      {/* Header */}
      <header
        className="flex-shrink-0 z-40 px-5 sm:px-8 pt-5 pb-4"
        style={{ backgroundColor: containerNeutralSolidSurface }}
      >
        {/* Segmented progress bar */}
        <div className="max-w-md mx-auto flex gap-1.5">
          {Array.from({ length: NUMBER_OF_STEPS }, (_, i) => (
            <div
              key={i}
              className="h-1 flex-1 rounded-full transition-all duration-500 ease-out"
              style={{
                backgroundColor:
                  i <= store.step ? accentColor : containerNeutralLightMedium,
                opacity: i < store.step ? 0.55 : i === store.step ? 1 : 0.35,
              }}
            />
          ))}
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 min-h-0 px-5 sm:px-8 pb-8 flex justify-center items-center relative z-10 overflow-y-auto">
        <div key={store.step} className="w-full max-w-md">
          {renderStep()}

          {submitError && (
            <div
              className="mt-6 px-5 py-4 rounded-2xl text-sm font-medium"
              style={{
                backgroundColor: "rgba(220, 54, 46, 0.08)",
                color: "#B02B25",
                border: "1px solid rgba(220, 54, 46, 0.15)",
              }}
            >
              {submitError}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer
        className="flex-shrink-0 px-5 sm:px-8 py-5 relative z-10"
        style={{
          backgroundColor: containerNeutralSolidSurface,
        }}
      >
        <div className="max-w-md mx-auto flex items-center justify-between gap-4">
          {store.step > 0 ? (
            <button
              onClick={handleBack}
              className="group flex items-center gap-2 px-6 py-3.5 rounded-full font-medium transition-all hover:scale-[1.02] active:scale-[0.98]"
              style={{
                backgroundColor: "rgba(2, 2, 16, 0.06)",
                color: textNeutralDefault,
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="transition-transform group-hover:-translate-x-0.5"
              >
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              {t("back") || "Back"}
            </button>
          ) : (
            <div />
          )}

          <button
            onClick={handleNext}
            disabled={!isCurrentStepValid || isSubmitting}
            className="group flex items-center gap-2 px-8 py-3.5 rounded-full font-gnarly-semibold transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
            style={{
              backgroundColor: accentColor,
              color: textNeutralOnPrimary,
            }}
          >
            {isSubmitting && (
              <div
                className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin"
                style={{
                  borderColor: textNeutralOnPrimary,
                  borderTopColor: "transparent",
                }}
              />
            )}
            {isSubmitting
              ? t("creating_account") || "Creating..."
              : isLastStep
                ? t("finish") || "Finish"
                : t("next") || "Next"}
            {!isSubmitting && !isLastStep && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="transition-transform group-hover:translate-x-0.5"
              >
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            )}
          </button>
        </div>
      </footer>

      {/* Turnstile verification modal */}
      {showTurnstileModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{
            backgroundColor: "rgba(2, 2, 16, 0.45)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
        >
          <div
            className="max-w-sm w-full p-6"
            style={{
              backgroundColor: containerNeutralSolidSurface,
              borderRadius: 20,
              boxShadow:
                "0 24px 80px rgba(2, 2, 16, 0.2), 0 0 0 1px rgba(2, 2, 16, 0.05)",
            }}
          >
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5"
              style={{ backgroundColor: `${accentColor}14` }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke={accentColor}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>

            <h2
              className="text-xl font-gnarly-semibold mb-2 text-center"
              style={{ color: textNeutralDefault }}
            >
              {t("verify_human_title") || "Quick verification"}
            </h2>
            <p
              className="text-sm leading-relaxed text-center mb-6"
              style={{ color: textNeutralMuted }}
            >
              {t("verify_human_desc_signup") ||
                "Please complete the check below to create your account."}
            </p>

            {turnstileError ? (
              <div className="flex flex-col items-center gap-3 mb-4">
                <p
                  className="text-sm text-center"
                  style={{ color: "#B02B25" }}
                >
                  {t("verify_human_error") ||
                    "Verification failed. Please try again."}
                </p>
                <button
                  onClick={() => {
                    posthog?.capture("turnstile_failed", {
                      surface: "signup_page",
                      step: store.step,
                      is_authenticated: !!user,
                    });
                    resetToken();
                  }}
                  className="px-6 py-2.5 rounded-full text-sm font-gnarly-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
                  style={{
                    backgroundColor: accentColor,
                    color: textNeutralOnPrimary,
                  }}
                >
                  {t("try_again") || "Try again"}
                </button>
              </div>
            ) : (
              <div className="flex justify-center mb-4">
                <TurnstileWidget
                  siteKey={siteKey}
                  widgetRef={widgetRef}
                  onSuccess={handleTurnstileSuccess}
                  onExpire={onExpire}
                  onError={() => {
                    posthog?.capture("turnstile_failed", {
                      surface: "signup_page",
                      step: store.step,
                      is_authenticated: !!user,
                    });
                    onError();
                  }}
                  appearance="always"
                />
              </div>
            )}

            <button
              onClick={() => {
                posthog?.capture("turnstile_cancelled", {
                  surface: "signup_page",
                  step: store.step,
                  is_authenticated: !!user,
                });
                setShowTurnstileModal(false);
                resetToken();
              }}
              className="w-full py-3 text-sm transition-opacity hover:opacity-70"
              style={{ color: textNeutralMuted }}
            >
              {t("cancel") || "Cancel"}
            </button>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />
    </div>
  );
};

export default SignUp;
